import { Predicate, Schema } from "effect";
import { AgentMessageMetadataSchema } from "./agent-accounting";
import { AgentActivityEventSchema } from "./agent-events";
import {
  ImageGenerationUiProjectionSchema,
  ProjectImageGenerationCommandSchema,
  type ImageGenerationUiProjection,
} from "./image-generation";
import {
  PROJECT_FILE_MESSAGE_MAX_ATTACHMENTS,
  PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES,
  ProjectFileMessageReferenceSchema,
} from "./project-files";
import { utf8ByteLength } from "./utf8";
import { QuestionnaireAnswerSchema } from "./questionnaire";
import {
  REVIEW_COMMENT_MESSAGE_MAX_ATTACHMENTS,
  REVIEW_COMMENT_MESSAGE_MAX_TOTAL_BYTES,
  ReviewCommentSchema,
  reviewCommentContextBytes,
} from "./review-comments";
import {
  ProjectDescriptionSchema,
  ProjectInstructionsSchema,
  ProjectNameSchema,
} from "./workspace";

export { utf8ByteLength } from "./utf8";

export const AgentStorageBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const DURABLE_AGENT_MESSAGE_MAX_BYTES = 256 * 1_024;
export const DURABLE_AGENT_EVENT_MAX_BYTES = 32 * 1_024;
export const DURABLE_MODEL_HISTORY_MAX_BYTES = 512 * 1_024;

const IdSchema = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(180));
export const EncodedAgentMessageSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= DURABLE_AGENT_MESSAGE_MAX_BYTES ||
      `must be at most ${DURABLE_AGENT_MESSAGE_MAX_BYTES} UTF-8 bytes`,
  ),
);
export const EncodedAgentMessageHistorySchema = Schema.Array(EncodedAgentMessageSchema).check(
  Schema.isMaxLength(50),
  Schema.makeFilter(
    (messages) =>
      messages.reduce((total, message) => total + utf8ByteLength(message), 0) <=
        DURABLE_MODEL_HISTORY_MAX_BYTES ||
      `must be at most ${DURABLE_MODEL_HISTORY_MAX_BYTES} UTF-8 bytes in total`,
  ),
);
const EncodedEventSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      utf8ByteLength(value) <= DURABLE_AGENT_EVENT_MAX_BYTES ||
      `must be at most ${DURABLE_AGENT_EVENT_MAX_BYTES} UTF-8 bytes`,
  ),
);

export const DurableAgentStatusSchema = Schema.Literals([
  "queued",
  "running",
  "waiting",
  "waiting-for-approval",
  "succeeded",
  "failed",
  "cancelled",
]);

const DurableAgentEventMessagePartSchema = Schema.Struct({
  type: Schema.Literal("data-agent-event"),
  id: Schema.optionalKey(IdSchema),
  data: AgentActivityEventSchema,
});

export const DurableImageGenerationMessagePartSchema = Schema.Struct({
  type: Schema.Literal("data-image-generation"),
  id: IdSchema,
  data: ImageGenerationUiProjectionSchema,
}).check(
  Schema.makeFilter(
    (part) => part.id === part.data.operationId || "image receipt ID must match its operation ID",
  ),
  Schema.makeFilter(
    (part) =>
      part.data.status === "ready" ||
      part.data.status === "failed" ||
      part.data.status === "cancelled" ||
      part.data.status === "outcome-unknown" ||
      "durable image receipts must be terminal",
  ),
);
export const isDurableImageGenerationMessagePart = Schema.is(
  DurableImageGenerationMessagePartSchema,
);

export const DurableImageGenerationRequestMessagePartSchema = Schema.Struct({
  type: Schema.Literal("data-image-generation-request"),
  data: ProjectImageGenerationCommandSchema,
});
const isDurableImageGenerationRequestMessagePart = Schema.is(
  DurableImageGenerationRequestMessagePartSchema,
);

const DurableOtherMessagePartSchema = Schema.Unknown.check(
  Schema.makeFilter(
    (value) =>
      !Predicate.hasProperty(value, "type") ||
      (value.type !== "data-agent-event" &&
        value.type !== "data-image-generation" &&
        value.type !== "data-image-generation-request" &&
        value.type !== "file" &&
        value.type !== "reasoning-file"),
    { title: "Non-canonical durable message part" },
  ),
);

export const DurableMessageEnvelopeSchema = Schema.Struct({
  id: IdSchema,
  role: Schema.Literals(["user", "assistant"]),
  metadata: Schema.optionalKey(AgentMessageMetadataSchema),
  parts: Schema.Array(
    Schema.Union([
      DurableAgentEventMessagePartSchema,
      DurableImageGenerationMessagePartSchema,
      DurableImageGenerationRequestMessagePartSchema,
      DurableOtherMessagePartSchema,
    ]),
  ).check(Schema.isMaxLength(512)),
}).check(
  Schema.makeFilter((message) => {
    const receiptCount = message.parts.filter(isDurableImageGenerationMessagePart).length;
    return (
      receiptCount === 0 ||
      message.role === "assistant" ||
      "only assistant messages may contain image generation receipts"
    );
  }),
  Schema.makeFilter((message) => {
    const operationIds = message.parts.flatMap((part) =>
      isDurableImageGenerationMessagePart(part) ? [part.data.operationId] : [],
    );
    return (
      new Set(operationIds).size === operationIds.length ||
      "must contain at most one image receipt per operation"
    );
  }),
  Schema.makeFilter((message) => {
    const requestCount = message.parts.filter(isDurableImageGenerationRequestMessagePart).length;
    if (requestCount === 0) return true;
    if (message.role !== "user") return "only user messages may contain image generation requests";
    return requestCount === 1 || "must contain at most one image generation request";
  }),
);

export function durableImageGenerationReceipts(
  message: DurableMessageEnvelope,
): ReadonlyArray<ImageGenerationUiProjection> {
  return message.parts.flatMap((part) =>
    isDurableImageGenerationMessagePart(part) ? [part.data] : [],
  );
}

const DurableUserTextPartSchema = Schema.Struct({
  type: Schema.Literal("text"),
  text: Schema.String,
});
export const DurableProjectFilePartSchema = Schema.Struct({
  type: Schema.Literal("data-project-file"),
  data: ProjectFileMessageReferenceSchema,
});
export const DurableQuestionnaireAnswerPartSchema = Schema.Struct({
  type: Schema.Literal("data-questionnaire-answer"),
  data: QuestionnaireAnswerSchema,
});
export const DurableReviewCommentPartSchema = Schema.Struct({
  type: Schema.Literal("data-review-comment"),
  data: ReviewCommentSchema,
});
export const DurableImageGenerationRequestPartSchema =
  DurableImageGenerationRequestMessagePartSchema;
const DurableUserMessagePartsSchema = Schema.Array(
  Schema.Union([
    DurableUserTextPartSchema,
    DurableProjectFilePartSchema,
    DurableQuestionnaireAnswerPartSchema,
    DurableReviewCommentPartSchema,
    DurableImageGenerationRequestPartSchema,
  ]),
).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(512),
  Schema.makeFilter(
    (parts) =>
      parts.some(
        (part) =>
          part.type === "data-project-file" ||
          part.type === "data-questionnaire-answer" ||
          part.type === "data-review-comment" ||
          part.type === "data-image-generation-request" ||
          part.text.trim().length > 0,
      ) || "must contain text or structured message context",
  ),
  Schema.makeFilter((parts) => {
    const references = parts.flatMap((part) =>
      part.type === "data-project-file" ? [part.data] : [],
    );
    return (
      references.length <= PROJECT_FILE_MESSAGE_MAX_ATTACHMENTS ||
      `must contain at most ${PROJECT_FILE_MESSAGE_MAX_ATTACHMENTS} project file references`
    );
  }),
  Schema.makeFilter((parts) => {
    const references = parts.flatMap((part) =>
      part.type === "data-project-file" ? [part.data] : [],
    );
    return (
      new Set(references.map((reference) => reference.fileRevisionId)).size === references.length ||
      "must not contain the same project file revision more than once"
    );
  }),
  Schema.makeFilter(
    (parts) =>
      parts.filter((part) => part.type === "data-questionnaire-answer").length <= 1 ||
      "must contain at most one questionnaire answer",
  ),
  Schema.makeFilter(
    (parts) =>
      parts.filter((part) => part.type === "data-image-generation-request").length <= 1 ||
      "must contain at most one image generation request",
  ),
  Schema.makeFilter((parts) => {
    const hasImageRequest = parts.some((part) => part.type === "data-image-generation-request");
    return (
      !hasImageRequest ||
      parts.every(
        (part) => part.type === "text" || part.type === "data-image-generation-request",
      ) ||
      "an image generation request may contain only display text and its canonical request"
    );
  }),
  Schema.makeFilter((parts) => {
    const comments = parts.flatMap((part) =>
      part.type === "data-review-comment" ? [part.data] : [],
    );
    return (
      comments.length <= REVIEW_COMMENT_MESSAGE_MAX_ATTACHMENTS ||
      `must contain at most ${REVIEW_COMMENT_MESSAGE_MAX_ATTACHMENTS} review comments`
    );
  }),
  Schema.makeFilter((parts) => {
    const comments = parts.flatMap((part) =>
      part.type === "data-review-comment" ? [part.data] : [],
    );
    return (
      new Set(comments.map((comment) => comment.commentId)).size === comments.length ||
      "must not contain the same review comment more than once"
    );
  }),
  Schema.makeFilter((parts) => {
    const reviewContextBytes = parts.reduce(
      (total, part) =>
        total + (part.type === "data-review-comment" ? reviewCommentContextBytes(part.data) : 0),
      0,
    );
    return (
      reviewContextBytes <= REVIEW_COMMENT_MESSAGE_MAX_TOTAL_BYTES ||
      `must contain at most ${REVIEW_COMMENT_MESSAGE_MAX_TOTAL_BYTES} review-context UTF-8 bytes`
    );
  }),
  Schema.makeFilter((parts) => {
    const referencedBytes = parts.reduce(
      (total, part) => total + (part.type === "data-project-file" ? part.data.byteSize : 0),
      0,
    );
    return (
      referencedBytes <= PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES ||
      `must reference at most ${PROJECT_FILE_MESSAGE_MAX_TOTAL_BYTES} project file bytes`
    );
  }),
);

export const areDurableUserMessagePartsEquivalent = Schema.toEquivalence(
  DurableUserMessagePartsSchema,
);

export const DurableSubmittedUserMessageSchema = Schema.Struct({
  id: IdSchema,
  role: Schema.Literal("user"),
  parts: DurableUserMessagePartsSchema,
});

export const DurableAgentEventSchema = Schema.Struct({
  eventId: IdSchema,
  sequence: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  agentId: IdSchema,
  parentAgentId: Schema.optionalKey(IdSchema),
  agentName: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  agentRole: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(120))),
  kind: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  status: DurableAgentStatusSchema,
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  encodedData: EncodedEventSchema,
  occurredAt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});

export const BeginAgentTurnRequestSchema = Schema.Struct({
  workspaceId: IdSchema,
  projectId: IdSchema,
  threadId: IdSchema,
  requestId: IdSchema,
  surface: Schema.Literal("projects"),
  rootAgentId: IdSchema,
  runner: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  provider: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  model: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  message: Schema.Struct({
    messageId: IdSchema,
    encodedMessage: EncodedAgentMessageSchema,
  }),
  branch: Schema.optionalKey(
    Schema.Struct({
      operation: Schema.Literals(["edit", "resend", "regenerate"]),
      sourceMessageId: IdSchema,
    }),
  ),
});

export const BeginAgentTurnResultSchema = Schema.Struct({
  runId: IdSchema,
  runGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  attemptId: IdSchema,
  attemptGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  surface: Schema.Literal("projects"),
  actorUserId: IdSchema,
  actorRole: Schema.Literals(["owner", "admin", "developer", "member", "billing", "guest"]),
  resourceContext: Schema.Struct({
    workspace: Schema.Struct({
      name: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
    }),
    project: Schema.Struct({
      name: ProjectNameSchema,
      description: Schema.optionalKey(ProjectDescriptionSchema),
      instructions: Schema.optionalKey(ProjectInstructionsSchema),
    }),
    thread: Schema.Struct({
      title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
    }),
  }),
  connections: Schema.Array(
    Schema.Struct({
      provider: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
      scope: Schema.Literals(["personal", "workspace"]),
      status: Schema.Literals(["connected", "expired", "pending", "error"]),
      displayName: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(160))),
      capabilities: Schema.Array(
        Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
      ).check(Schema.isMaxLength(64)),
    }),
  ).check(Schema.isMaxLength(100)),
  entitlements: Schema.Struct({
    agentAccess: Schema.Boolean,
    apiAccess: Schema.Boolean,
    connectorAccess: Schema.Boolean,
    browserAgent: Schema.Boolean,
    mcpAccess: Schema.Boolean,
    maxConcurrentAgents: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
    monthlyCreditsMicros: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
    requestsPerMinute: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
    currentPeriodEndsAt: Schema.optionalKey(Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))),
    sourceUpdatedAt: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  }),
  encodedMessages: EncodedAgentMessageHistorySchema,
});

export const AppendAgentEventsRequestSchema = Schema.Struct({
  workspaceId: IdSchema,
  runId: IdSchema,
  attemptId: IdSchema,
  runGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  attemptGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  leaseId: IdSchema,
  leaseOwner: IdSchema,
  now: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  events: Schema.Array(DurableAgentEventSchema).check(Schema.isMaxLength(64)),
});

export const CompleteAgentRunRequestFields = {
  workspaceId: IdSchema,
  runId: IdSchema,
  attemptId: IdSchema,
  attemptGeneration: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  status: Schema.Literals(["succeeded", "failed", "cancelled"]),
  responseMessage: Schema.optionalKey(
    Schema.Struct({
      messageId: IdSchema,
      encodedMessage: EncodedAgentMessageSchema,
      status: Schema.Literals(["complete", "interrupted"]),
    }),
  ),
  events: Schema.Array(DurableAgentEventSchema).check(Schema.isMaxLength(64)),
  summary: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(2_000))),
  errorCode: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(120))),
  errorMessage: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(2_000))),
} as const;

export function completeAgentRunResponseStatusConsistency(input: {
  readonly status: "succeeded" | "failed" | "cancelled";
  readonly responseMessage?: { readonly status: "complete" | "interrupted" } | undefined;
}): true | string {
  if (input.status === "succeeded") {
    return (
      input.responseMessage?.status === "complete" ||
      "a succeeded run requires a complete assistant message"
    );
  }
  return (
    input.responseMessage === undefined ||
    input.responseMessage.status === "interrupted" ||
    "a failed or cancelled run may include only an interrupted assistant message"
  );
}

export const CompleteAgentRunRequestSchema = Schema.Struct(CompleteAgentRunRequestFields).check(
  Schema.makeFilter(completeAgentRunResponseStatusConsistency),
);

export const decodeBeginAgentTurnResult = Schema.decodeUnknownSync(
  BeginAgentTurnResultSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeDurableMessageEnvelope = Schema.decodeUnknownSync(
  DurableMessageEnvelopeSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeDurableSubmittedUserMessage = Schema.decodeUnknownSync(
  DurableSubmittedUserMessageSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeBeginAgentTurnRequest = Schema.decodeUnknownSync(
  BeginAgentTurnRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeAppendAgentEventsRequest = Schema.decodeUnknownSync(
  AppendAgentEventsRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const decodeCompleteAgentRunRequest = Schema.decodeUnknownSync(
  CompleteAgentRunRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const encodeBeginAgentTurnRequest = Schema.encodeSync(
  BeginAgentTurnRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const encodeAppendAgentEventsRequest = Schema.encodeSync(
  AppendAgentEventsRequestSchema,
  AgentStorageBoundaryParseOptions,
);
export const encodeCompleteAgentRunRequest = Schema.encodeSync(
  CompleteAgentRunRequestSchema,
  AgentStorageBoundaryParseOptions,
);

export type DurableAgentEvent = typeof DurableAgentEventSchema.Type;
export type DurableMessageEnvelope = typeof DurableMessageEnvelopeSchema.Type;
export type DurableSubmittedUserMessage = typeof DurableSubmittedUserMessageSchema.Type;
export type BeginAgentTurnRequest = typeof BeginAgentTurnRequestSchema.Type;
export type BeginAgentTurnResult = typeof BeginAgentTurnResultSchema.Type;
export type AgentResourceContext = BeginAgentTurnResult["resourceContext"];
export type AppendAgentEventsRequest = typeof AppendAgentEventsRequestSchema.Type;
export type CompleteAgentRunRequest = typeof CompleteAgentRunRequestSchema.Type;
